import TreeCollection from '@hh.ru/magritte-ui-tree-selector/collection/treeCollection';
import { filterMissingIds, filterParents } from '@hh.ru/magritte-ui-tree-selector/collection/treeCollectionHelper';
import { AdditionalDefault, IdCollectionPredicate } from '@hh.ru/magritte-ui-tree-selector/collection/types';
import createSingleValueToggler from '@hh.ru/magritte-ui-tree-selector/strategy/createSingleValueToggler';
import createTreeCollectionToggler from '@hh.ru/magritte-ui-tree-selector/strategy/createTreeCollectionToggler';
import dummyToggle from '@hh.ru/magritte-ui-tree-selector/strategy/dummyToggle';
import { StrategyOptions, Toggler } from '@hh.ru/magritte-ui-tree-selector/strategy/types';

const _filterSelectable = <Additional extends AdditionalDefault>(
    collection: TreeCollection<Additional>,
    ids: string[],
    checkSelectable: IdCollectionPredicate
) => {
    return ids.filter((id) => checkSelectable(id, collection));
};

const defaultCheckSelectable = () => true;

/**
 * Оставляет в массиве только последний ID
 */
const _filterMultipleIds = (ids: string[]) => {
    if (ids.length > 1) {
        return [ids[ids.length - 1]];
    }
    return ids;
};

/**
 * Стратегия переключения флага `selected` для иерархической коллекции.
 */
class SelectionStrategy<Additional extends AdditionalDefault = never> {
    private collection: TreeCollection<Additional>;
    private options: StrategyOptions;
    private checkSelectable: IdCollectionPredicate;
    private toggle: Toggler;

    constructor(collection: TreeCollection<Additional>, options: StrategyOptions) {
        this.collection = collection;
        this.options = options;
        this.checkSelectable = options.checkSelectable || defaultCheckSelectable;
        this.toggle = options.singleChoice
            ? createSingleValueToggler()
            : createTreeCollectionToggler(collection, this.checkSelectable);
    }

    add(items: Set<string>, ids: string[]): void {
        let filteredIds = filterMissingIds(this.collection, ids);

        if (this.options.singleChoice) {
            filteredIds = _filterMultipleIds(filteredIds);
        }
        if (this.options.leavesOnly) {
            filteredIds = filterParents(this.collection, filteredIds);
        }
        if (this.options.checkSelectable) {
            filteredIds = _filterSelectable(this.collection, filteredIds, this.options.checkSelectable);
        }

        if (this.options.leavesOnly && !this.options.singleChoice) {
            // Тут не нужно выбирать или сбрасывать родителей
            filteredIds.forEach((id) => {
                dummyToggle(items, id, true);
            });
        } else {
            filteredIds.forEach((id) => {
                this.toggle(items, id, true);
            });
        }
    }

    remove(items: Set<string>, ids: string[]): void {
        const filteredIds = filterMissingIds(this.collection, ids);

        filteredIds.forEach((id) => {
            this.toggle(items, id, false);
        });
    }

    set(items: Set<string>, ids: string[]): void {
        items.clear();
        this.add(items, ids);
    }
}

export default SelectionStrategy;
